import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Backdrop
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import transactionAPI from '../Network/Transaction';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

const cookies = new Cookies();
const STORAGE_BASE_URL = 'https://storage.googleapis.com/';

// Time-based theme configuration
// Accepts an optional datetime string (format: "YYYY-MM-DD HH:mm:ss")
const getTimeOfDayTheme = (datetimeStr = null) => {
    let hour;
    let displayTime;

    if (datetimeStr) {
        // Parse the transaction datetime
        try {
            const timePart = datetimeStr.split(' ')[1]; // "HH:mm:ss"
            if (timePart) {
                hour = parseInt(timePart.split(':')[0], 10);
                displayTime = timePart.substring(0, 5); // "HH:mm"
            } else {
                hour = new Date().getHours();
                displayTime = new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
            }
        } catch (e) {
            hour = new Date().getHours();
            displayTime = new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
        }
    } else {
        hour = new Date().getHours();
        displayTime = new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
    }

    // Dawn: 5:00 - 6:59
    if (hour >= 5 && hour < 7) {
        return {
            name: 'dawn',
            label: 'الفجر',
            icon: WbTwilightIcon,
            background: 'linear-gradient(135deg, #1a1a2e 0%, #4a3f6b 30%, #e8a87c 70%, #f7d79c 100%)',
            cardBackground: 'linear-gradient(145deg, #2d2654 0%, #4a3f6b 100%)',
            accentColor: '#e8a87c',
            iconColor: '#f7d79c',
            displayTime
        };
    }
    // Morning: 7:00 - 11:59
    if (hour >= 7 && hour < 12) {
        return {
            name: 'morning',
            label: 'الصباح',
            icon: LightModeIcon,
            background: 'linear-gradient(135deg, #87CEEB 0%, #98D8C8 30%, #F7DC6F 70%, #fff5cc 100%)',
            cardBackground: 'linear-gradient(145deg, #5dade2 0%, #76d7c4 100%)',
            accentColor: '#F7DC6F',
            iconColor: '#f39c12',
            displayTime
        };
    }
    // Noon/Afternoon: 12:00 - 16:59
    if (hour >= 12 && hour < 17) {
        return {
            name: 'noon',
            label: 'الظهيرة',
            icon: WbSunnyIcon,
            background: 'linear-gradient(135deg, #2980B9 0%, #6DD5FA 30%, #FFFFFF 70%, #fff8dc 100%)',
            cardBackground: 'linear-gradient(145deg, #3498db 0%, #5dade2 100%)',
            accentColor: '#f1c40f',
            iconColor: '#f39c12',
            displayTime
        };
    }
    // Sunset: 17:00 - 18:59
    if (hour >= 17 && hour < 19) {
        return {
            name: 'sunset',
            label: 'الغروب',
            icon: WbTwilightIcon,
            background: 'linear-gradient(135deg, #1a1a2e 0%, #e74c3c 25%, #f39c12 50%, #f7dc6f 75%, #fdf6e3 100%)',
            cardBackground: 'linear-gradient(145deg, #c0392b 0%, #e74c3c 100%)',
            accentColor: '#f39c12',
            iconColor: '#f7dc6f',
            displayTime
        };
    }
    // Dusk: 19:00 - 20:59
    if (hour >= 19 && hour < 21) {
        return {
            name: 'dusk',
            label: 'الشفق',
            icon: NightsStayIcon,
            background: 'linear-gradient(135deg, #0c0c1e 0%, #2c3e50 30%, #8e44ad 60%, #c39bd3 100%)',
            cardBackground: 'linear-gradient(145deg, #1a1a2e 0%, #2c3e50 100%)',
            accentColor: '#9b59b6',
            iconColor: '#c39bd3',
            displayTime
        };
    }
    // Night: 21:00 - 4:59
    return {
        name: 'night',
        label: 'الليل',
        icon: DarkModeIcon,
        background: 'linear-gradient(135deg, #0a0a15 0%, #0f0f23 30%, #1a1a3e 60%, #2d2d5a 100%)',
        cardBackground: 'linear-gradient(145deg, #0f0f23 0%, #1a1a3e 100%)',
        accentColor: '#3498db',
        iconColor: '#5dade2',
        displayTime
    };
};

// Helper function to build full image URL
const getFullImageUrl = (imagePath) => {
    if (!imagePath || imagePath.trim() === '') return null;
    // If already a full URL, return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }
    return `${STORAGE_BASE_URL}${imagePath}`;
};

const TransactionImagesModal = ({ open, onClose, transactionId, transactionDatetime }) => {
    const [t] = useTranslation('common');
    const [loading, setLoading] = useState(false);
    const [images, setImages] = useState(null);
    const [zoomedImage, setZoomedImage] = useState(null);
    const [theme, setTheme] = useState(getTimeOfDayTheme());

    // Update theme when modal opens based on transaction datetime
    useEffect(() => {
        if (open) {
            setTheme(getTimeOfDayTheme(transactionDatetime));
        }
    }, [open, transactionDatetime]);

    const headerObject = {
        username: cookies.get('user_name') || null,
        user_id: cookies.get('user_id') || null,
        account_name: cookies.get('account_name') || null,
        account_id: cookies.get('account_id') || null,
        authHash: cookies.get('authHash') || null,
        sid: cookies.get('sid') || null,
        baseurl: cookies.get('baseUrl') || null,
        id: cookies.get('id') || null,
    };

    useEffect(() => {
        console.log("TransactionImagesModal opened", { open, transactionId });
        if (open && transactionId) {
            loadImages();
        }
    }, [open, transactionId]);

    const loadImages = async () => {
        setLoading(true);
        try {
            const result = await transactionAPI.getMizanTransactionImages({
                id: transactionId,
                params: headerObject
            });
            if (result?.status && result?.data?.status) {
                setImages(result?.data?.data);
            } else {
                setImages(null);
            }
        } catch (error) {
            console.error('Error loading images:', error);
            setImages(null);
        }
        setLoading(false);
    };

    const handleClose = () => {
        setImages(null);
        setZoomedImage(null);
        onClose();
    };

    const ImageCard = ({ src, label }) => {
        const hasImage = src && src.trim() !== '';

        return (
            <Box
                sx={{
                    position: 'relative',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: theme.cardBackground,
                    boxShadow: `0 4px 20px rgba(0, 0, 0, 0.3)`,
                    border: `1px solid ${theme.accentColor}33`,
                    height: '100%',
                    width: '100%',
                    cursor: hasImage ? 'pointer' : 'default',
                    transition: 'all 0.3s ease',
                    '&:hover': hasImage ? {
                        transform: 'scale(1.01)',
                        boxShadow: `0 8px 30px ${theme.accentColor}55`,
                    } : {},
                }}
                onClick={() => hasImage && setZoomedImage(src)}
            >
                {hasImage ? (
                    <>
                        <img
                            src={src}
                            alt={label}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <Box
                            sx={{
                                display: 'none',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <BrokenImageIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                                فشل تحميل الصورة
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(0, 120, 255, 0.9)',
                                borderRadius: '8px',
                                padding: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <ZoomInIcon sx={{ fontSize: 16, color: '#fff' }} />
                        </Box>
                    </>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <PhotoCameraIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)' }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', fontFamily: 'Cairo-Medium' }}>
                            لا توجد صورة
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                        padding: '16px 12px 8px',
                    }}
                >
                    <Typography
                        sx={{
                            color: '#fff',
                            fontSize: '13px',
                            fontWeight: 600,
                            fontFamily: 'Cairo-Bold',
                            textAlign: 'center',
                        }}
                    >
                        {label}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const hasAnyImage = images && (
        images.image_one_empty ||
        images.image_two_empty ||
        images.image_one_full ||
        images.image_two_full
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.background,
                        boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5)',
                        border: `1px solid ${theme.accentColor}33`,
                        transition: 'background 0.5s ease',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.accentColor}33`,
                        padding: '16px 24px',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '12px',
                                background: `linear-gradient(135deg, ${theme.accentColor} 0%, ${theme.iconColor} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 4px 15px ${theme.accentColor}66`,
                            }}
                        >
                            <theme.icon sx={{ color: '#fff', fontSize: 22 }} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography
                                sx={{
                                    fontFamily: 'Cairo-Bold',
                                    fontSize: '18px',
                                    color: '#fff',
                                }}
                            >
                                📷 صور المستند
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: 'Cairo-Medium',
                                    fontSize: '12px',
                                    color: theme.iconColor,
                                    opacity: 0.9,
                                }}
                            >
                                {theme.label} • {theme.displayTime}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ padding: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <CircularProgress sx={{ color: theme.accentColor }} />
                            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Cairo-Medium' }}>
                                جاري تحميل الصور...
                            </Typography>
                        </Box>
                    ) : !hasAnyImage ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <PhotoCameraIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.2)' }} />
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Cairo-Medium', fontSize: '16px' }}>
                                لا توجد صور لهذا المستند
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gridTemplateRows: 'repeat(2, calc(50% - 8px))',
                                gap: '16px',
                                height: 'calc(90vh - 180px)', // Account for header, footer, and padding
                                width: '100%',
                            }}
                        >
                            <ImageCard src={getFullImageUrl(images?.image_one_empty)} label="صورة الوزن الفارغ #1" />
                            <ImageCard src={getFullImageUrl(images?.image_two_empty)} label="صورة الوزن الفارغ #2" />
                            <ImageCard src={getFullImageUrl(images?.image_one_full)} label="صورة الوزن المحمل #1" />
                            <ImageCard src={getFullImageUrl(images?.image_two_full)} label="صورة الوزن المحمل #2" />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(135deg, #0078ff 0%, #00d4ff 100%)',
                            borderRadius: '12px',
                            fontFamily: 'Cairo-Bold',
                            padding: '10px 32px',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0066dd 0%, #00b8dd 100%)',
                            }
                        }}
                    >
                        إغلاق
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Zoom Modal */}
            <Backdrop
                open={!!zoomedImage}
                onClick={() => setZoomedImage(null)}
                sx={{
                    zIndex: 9999,
                    backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    cursor: 'zoom-out',
                }}
            >
                {zoomedImage && (
                    <Box
                        sx={{
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            position: 'relative',
                        }}
                    >
                        <img
                            src={zoomedImage}
                            alt="Zoomed"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                borderRadius: '8px',
                            }}
                        />
                        <IconButton
                            onClick={() => setZoomedImage(null)}
                            sx={{
                                position: 'absolute',
                                top: -50,
                                right: 0,
                                color: '#fff',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                )}
            </Backdrop>
        </>
    );
};

export default TransactionImagesModal;
